import {
  FilterList as FilterListIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  SxProps,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { getAssemblyConstituencyGrid } from "../api/requests/assemblyConstituency";
import { getUpdatedGroup } from "../api/requests/groups";
import { getAllRanks } from "../api/requests/ranks";
import { createGroupForUsers, getUsers } from "../api/requests/users";
import { getZoneGrid } from "../api/requests/zones";
import { useAlert } from "../context/AlertContext";
import { User } from "../context/AuthContext";
import { labelStyles } from "../pages/createUser";
import { PaginationType } from "../pages/Groups";
import {
  AssemblyConstituencyType,
  RankWithUsersType,
  RIOType,
  ZoneType,
} from "../utils/types";
import CommonDialog from "./common/CommonDialog";
import { getRIOGrid } from "../api/requests/RIO";
import { FILTER_PAGE_SIZE } from "../utils/constants";

interface FilterOption {
  id: number;
  name: string;
}

interface AppliedFilter {
  type: "rank" | "assembly" | "zone" | "rio";
  id: number;
  name: string;
}

const CreateOrEditGroup = ({
  setCreateEditDialogOpen,
  isEdit = false,
  editGroupId,
  groupSelectedUsers,
  selectedGroupName,
}: {
  setCreateEditDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isEdit?: boolean;
  editGroupId?: number;
  groupSelectedUsers: number[];
  selectedGroupName: string;
}) => {
  const { showAlert } = useAlert();

  const [groupName, setGroupName] = useState(selectedGroupName || "");
  const [selectedUsers, setSelectedUsers] = useState<number[]>([
    ...groupSelectedUsers,
  ]);

  const [paginationModel, setPaginationModel] = useState<PaginationType>({
    page: 0,
    pageSize: 10,
  });
  const [userList, setUserList] = useState<User[]>([]);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [userLoading, setUserLoading] = useState<boolean>(false);

  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [selectedFilterType, setSelectedFilterType] = useState<
    "rank" | "assembly" | "zone" | "rio" | null
  >(null);
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilter[]>([]);
  const [filterDialogOpen, setFilterDialogOpen] = useState<boolean>(false);

  const [searchText, setSearchText] = useState<string>("");

  const [filterOptions, setFilterOptions] = useState<FilterOption[]>([]);
  const [selectedFilterIds, setSelectedFilterIds] = useState<number[]>([]);

  const filterMenuOpen = Boolean(filterAnchorEl);

  // 👉 Added for infinite scroll main user list
  const listRef = useRef<HTMLDivElement | null>(null);

  // --- New: paginated states for each filter type ---
  // Lists
  const [ranksList, setRanksList] = useState<RankWithUsersType[]>([]);
  const [riosList, setRiosList] = useState<RIOType[]>([]);
  const [zonesList, setZonesList] = useState<ZoneType[]>([]);
  const [assemblyList, setAssemblyList] = useState<AssemblyConstituencyType[]>(
    []
  );

  // pages
  const [ranksPage, setRanksPage] = useState<number>(0);
  const [riosPage, setRiosPage] = useState<number>(0);
  const [zonesPage, setZonesPage] = useState<number>(0);
  const [assemblyPage, setAssemblyPage] = useState<number>(0);

  // separate search text for filter dialog (doesn't affect main user search)
  const [filterSearchText, setFilterSearchText] = useState<string>("");

  // loading states for filter lists
  const [filterLoading, setFilterLoading] = useState<boolean>(false);

  // refs for filter scroll
  const filterListRef = useRef<HTMLDivElement | null>(null);

  // --- Users fetching (keeps existing logic but uses applied filters) ---
  const fetchUsers = useCallback(
    async (resetPage = false) => {
      try {
        if (resetPage) setUserLoading(true);

        const page = resetPage ? 0 : paginationModel.page;

        const filterParams: any = {
          page,
          size: paginationModel.pageSize,
          q: searchText.trim(),
          activeUsers: true,
        };

        filterParams.rankIds = appliedFilters
          .filter((f) => f.type === "rank")
          .map((f) => f.id);

        filterParams.assemblyConstituencyIds = appliedFilters
          .filter((f) => f.type === "assembly")
          .map((f) => f.id);

        filterParams.zoneIds = appliedFilters
          .filter((f) => f.type === "zone")
          .map((f) => f.id);

        filterParams.rioIds = appliedFilters
          .filter((f) => f.type === "rio")
          .map((f) => f.id);

        const usersResponse = await getUsers(filterParams);

        if (usersResponse.status === 200) {
          const newUsers: User[] = usersResponse.data.users || [];
          const activeUsers = newUsers.filter(
            (user) => user.status === "Active"
          );
          if (resetPage) {
            setUserList(activeUsers);
          } else {
            setUserList((prev) => [...prev, ...activeUsers]);
          }

          setTotalUsers(usersResponse.data.totalCount || 0);
        }
      } catch (error) {
        console.error("❌ Error fetching users:", error);
        showAlert("Failed to fetch users", "error");
      } finally {
        if (resetPage) setUserLoading(false);
      }
    },
    [paginationModel.page, paginationModel.pageSize, searchText, appliedFilters]
  );

  // --- Main infinite scroll for users ---
  const handleScroll = () => {
    if (!listRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = listRef.current;

    const reachedBottom = scrollTop + clientHeight >= scrollHeight - 50;

    if (reachedBottom && userList.length < totalUsers && !userLoading) {
      setPaginationModel((prev) => ({
        ...prev,
        page: prev.page + 1,
      }));
    }
  };
  // when page increases, fetch next page of users
  useEffect(() => {
    fetchUsers(paginationModel.page === 0);
  }, [searchText, appliedFilters, paginationModel.page, fetchUsers]);

  // --- Filter fetchers (paginated) ---
  const fetchRanks = useCallback(
    async (reset = false, q = "") => {
      try {
        setFilterLoading(true);
        const pageToFetch = reset ? 0 : ranksPage;
        const res = await getAllRanks({
          page: pageToFetch,
          size: FILTER_PAGE_SIZE,
          q,
        });
        if (res.status === 200) {
          const newRanks: RankWithUsersType[] = res.data.ranks || [];
          setRanksList((prev) => (reset ? newRanks : [...prev, ...newRanks]));
          if (reset) setRanksPage(0);
        }
      } catch (err) {
        console.error("❌ Error fetching ranks:", err);
        showAlert("Failed to load ranks", "error");
      } finally {
        setFilterLoading(false);
      }
    },
    [ranksPage, showAlert]
  );

  const fetchRIOs = useCallback(
    async (reset = false, searchQuery = "") => {
      try {
        setFilterLoading(true);
        const pageToFetch = reset ? 0 : riosPage;
        const res = await getRIOGrid({
          page: pageToFetch,
          size: FILTER_PAGE_SIZE,
          searchQuery,
        });
        if (res.status === 200) {
          const newRios: RIOType[] = res.data.rio || [];
          setRiosList((prev) => (reset ? newRios : [...prev, ...newRios]));
          if (reset) setRiosPage(0);
        }
      } catch (err) {
        console.error("❌ Error fetching RIOs:", err);
        showAlert("Failed to load RIOs", "error");
      } finally {
        setFilterLoading(false);
      }
    },
    [riosPage, showAlert]
  );

  const fetchZones = useCallback(
    async (reset = false, searchQuery = "") => {
      try {
        setFilterLoading(true);
        const pageToFetch = reset ? 0 : zonesPage;
        const res = await getZoneGrid({
          page: pageToFetch,
          size: FILTER_PAGE_SIZE,
          searchQuery,
        });
        if (res.status === 200) {
          console.log("resres", res);
          const newZones: ZoneType[] = res.data.zones || [];
          setZonesList((prev) => (reset ? newZones : [...prev, ...newZones]));
          if (reset) setZonesPage(0);
        }
      } catch (err) {
        console.error("❌ Error fetching zones:", err);
        showAlert("Failed to load zones", "error");
      } finally {
        setFilterLoading(false);
      }
    },
    [zonesPage, showAlert]
  );

  const fetchAssemblyConstituencies = useCallback(
    async (reset = false, searchQuery = "") => {
      try {
        setFilterLoading(true);
        const pageToFetch = reset ? 0 : assemblyPage;
        const res = await getAssemblyConstituencyGrid({
          page: pageToFetch,
          size: FILTER_PAGE_SIZE,
          searchQuery,
        });
        if (res.status === 200) {
          const newAssembly: AssemblyConstituencyType[] =
            res.data.assemblyConstituency || [];
          setAssemblyList((prev) =>
            reset ? newAssembly : [...prev, ...newAssembly]
          );
          if (reset) setAssemblyPage(0);
        }
      } catch (err) {
        console.error("❌ Error fetching assembly list:", err);
        showAlert("Failed to load assembly constituencies", "error");
      } finally {
        setFilterLoading(false);
      }
    },
    [assemblyPage, showAlert]
  );

  // When pages for filter lists increment, fetch more
  useEffect(() => {
    if (!filterDialogOpen) return;
    if (selectedFilterType === "rank") fetchRanks(false, filterSearchText);
  }, [ranksPage]);

  useEffect(() => {
    if (!filterDialogOpen) return;
    if (selectedFilterType === "rio") fetchRIOs(false, filterSearchText);
  }, [riosPage]);

  useEffect(() => {
    if (!filterDialogOpen) return;
    if (selectedFilterType === "zone") fetchZones(false, filterSearchText);
  }, [zonesPage]);

  useEffect(() => {
    if (!filterDialogOpen) return;
    if (selectedFilterType === "assembly")
      fetchAssemblyConstituencies(false, filterSearchText);
  }, [assemblyPage]);

  // --- open filter selection and initial load ---
  const handleFilterTypeSelect = (
    filterType: "rank" | "assembly" | "zone" | "rio"
  ) => {
    setSelectedFilterType(filterType);
    setSelectedFilterIds([]);
    setFilterSearchText("");
    // reset lists for that filter type and load first page
    if (filterType === "rank") {
      setRanksList([]);
      setRanksPage(0);
      fetchRanks(true, "");
    }
    if (filterType === "rio") {
      setRiosList([]);
      setRiosPage(0);
      fetchRIOs(true, "");
    }
    if (filterType === "zone") {
      setZonesList([]);
      setZonesPage(0);
      fetchZones(true, "");
    }
    if (filterType === "assembly") {
      setAssemblyList([]);
      setAssemblyPage(0);
      fetchAssemblyConstituencies(true, "");
    }

    setFilterDialogOpen(true);
    handleFilterMenuClose();
  };

  const handleApplyFilters = () => {
    const newFilters: AppliedFilter[] = [];

    selectedFilterIds.forEach((filterId) => {
      const option = filterOptions.find((opt) => opt.id === filterId);
      if (option) {
        newFilters.push({
          type: selectedFilterType!,
          id: option.id,
          name: option.name,
        });
      }
    });

    const updatedFilters = appliedFilters.filter(
      (f) => f.type !== selectedFilterType
    );
    setAppliedFilters([...updatedFilters, ...newFilters]);

    setFilterDialogOpen(false);
    setSelectedFilterIds([]);

    // re-fetch users with applied filters
    fetchUsers(true);
  };

  const handleCancelFilters = () => {
    setFilterDialogOpen(false);
    setSelectedFilterIds([]);
  };

  const removeFilter = (filterToRemove: AppliedFilter) => {
    const updatedFilters = appliedFilters.filter(
      (f) => !(f.type === filterToRemove.type && f.id === filterToRemove.id)
    );
    setAppliedFilters(updatedFilters);
    fetchUsers(true);
  };

  const clearAllFilters = () => {
    setAppliedFilters([]);
    fetchUsers(true);
  };

  // toggle selected users
  const handleUserToggle = (userId: number) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  // main search handlers (for users)
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    fetchUsers(true);
  };

  // save group
  const handleSaveGroup = async () => {
    setUserLoading(true);
    if (!groupName.trim()) {
      showAlert("Group name is required.", "error");
      setUserLoading(false);
      return;
    }
    if (selectedUsers.length === 0) {
      showAlert("Please select at least one user.", "error");
      setUserLoading(false);
      return;
    }

    try {
      const createGroupRes = await createGroupForUsers({
        groupName: groupName.trim(),
        memberUserIds: selectedUsers,
      });
      if (createGroupRes.status === 200) {
        setCreateEditDialogOpen(false);
        setGroupName("");
        setSelectedUsers([]);
        setAppliedFilters([]);
        setSearchText("");
        showAlert("Group created successfully.", "success");
      }
    } catch (err) {
      console.error("❌ Error creating group:", err);
      showAlert("Failed to create group.", "error");
    } finally {
      setUserLoading(false);
    }
  };

  // edit group
  const handleEditGroup = async () => {
    setUserLoading(true);
    if (!editGroupId) {
      showAlert("Group ID is missing.", "error");
      setUserLoading(false);
      return;
    }
    try {
      const updateGroupRes = await getUpdatedGroup({
        groupName,
        memberUserIds: selectedUsers,
        groupId: editGroupId,
      });
      if (updateGroupRes.status == 200) {
        setCreateEditDialogOpen(false);
        setGroupName("");
        setSelectedUsers([]);
        setAppliedFilters([]);
        setSearchText("");
        showAlert("Group edited successfully.", "success");
      }
    } catch (err) {
      console.error("❌ Error editing group:", err);
      showAlert("Failed to edit group.", "error");
    } finally {
      setUserLoading(false);
    }
  };

  const handleFilterMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterMenuClose = () => {
    setFilterAnchorEl(null);
  };

  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setUserList([]);
  }, [searchText, appliedFilters]);

  // fetch users on mount
  useEffect(() => {
    fetchUsers(true);
  }, []);

  // Update filterOptions whenever the corresponding list changes or selectedFilterType changes
  useEffect(() => {
    let opts: FilterOption[] = [];
    if (!selectedFilterType) {
      setFilterOptions([]);
      return;
    }

    if (selectedFilterType === "rank") {
      opts = ranksList.map((r) => ({
        id: r.rankId,
        name: r.rankName,
      }));
    } else if (selectedFilterType === "rio") {
      opts = riosList.map((r) => ({
        id: r.rioId,
        name: r.rioName,
      }));
    } else if (selectedFilterType === "zone") {
      opts = zonesList.map((z) => ({
        id: z.zoneId,
        name: z.zoneName,
      }));
    } else if (selectedFilterType === "assembly") {
      opts = assemblyList.map((a) => ({
        id: a.assemblyConstituencyId,
        name: a.assemblyConstituencyName,
      }));
    }

    setFilterOptions(opts);
  }, [
    ranksList,
    riosList,
    zonesList,
    assemblyList,
    selectedFilterType,
    filterDialogOpen,
  ]);

  // handler for filter dialog scroll to load next page
  const handleFilterScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const bottom =
      target.scrollHeight - target.scrollTop <= target.clientHeight + 20;

    if (!bottom) return;

    if (selectedFilterType === "rank") {
      setRanksPage((p) => p + 1);
    } else if (selectedFilterType === "rio") {
      setRiosPage((p) => p + 1);
    } else if (selectedFilterType === "zone") {
      setZonesPage((p) => p + 1);
    } else if (selectedFilterType === "assembly") {
      setAssemblyPage((p) => p + 1);
    }
  };

  // Debounced filter search (300ms)
  useEffect(() => {
    if (!filterDialogOpen || !selectedFilterType) return;

    const t = setTimeout(() => {
      // reset lists & pages and refetch with q = filterSearchText
      if (selectedFilterType === "rank") {
        setRanksList([]);
        setRanksPage(0);
        fetchRanks(true, filterSearchText);
      } else if (selectedFilterType === "rio") {
        setRiosList([]);
        setRiosPage(0);
        fetchRIOs(true, filterSearchText);
      } else if (selectedFilterType === "zone") {
        setZonesList([]);
        setZonesPage(0);
        fetchZones(true, filterSearchText);
      } else if (selectedFilterType === "assembly") {
        setAssemblyList([]);
        setAssemblyPage(0);
        fetchAssemblyConstituencies(true, filterSearchText);
      }
    }, 300);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSearchText]);

  const isUserSelected = (userId: number) => selectedUsers.includes(userId);

  return (
    <Box>
      <CommonDialog
        title={isEdit ? "Edit Group" : "Create Group"}
        cancelButton={() => setCreateEditDialogOpen(false)}
        actionButtonTitle="Save"
        cancelButtonTitle="Cancel"
        actionButton={isEdit ? handleEditGroup : handleSaveGroup}
        disableActionButton={!groupName.trim() || selectedUsers.length === 0}
      >
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: "14px", mb: 1 }}>
            Group Name <span style={{ color: "red" }}>*</span>
          </Typography>
          <TextField
            fullWidth
            margin="dense"
            name="groupName"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            slotProps={{
              input: { sx: labelStyles },
            }}
            placeholder="Enter group name"
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <form onSubmit={handleSearchSubmit}>
            <TextField
              fullWidth
              placeholder="Search users by name, email, or mobile..."
              value={searchText}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleFilterMenuClick}
                      disabled={userLoading}
                    >
                      <FilterListIcon />
                      {appliedFilters.length > 0 && (
                        <Chip
                          label={appliedFilters.length}
                          size="small"
                          color="primary"
                          sx={{ ml: 1, height: 20, fontSize: "0.7rem" }}
                        />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
          </form>

          {appliedFilters.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
              {appliedFilters.map((filter) => (
                <Chip
                  key={`${filter.type}-${filter.id}`}
                  label={filter.name}
                  onDelete={() => removeFilter(filter)}
                  color="primary"
                  size="small"
                  variant="outlined"
                />
              ))}
              <Button
                size="small"
                onClick={clearAllFilters}
                sx={{ ml: 1, textTransform: "none" }}
              >
                Clear All
              </Button>
            </Box>
          )}
        </Box>

        <Box sx={scrollStyles} onScrollCapture={handleScroll} ref={listRef}>
          <Box sx={{ minHeight: 410, pb: 4 }}>
            {userLoading && paginationModel.page === 0 ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : userList.length === 0 ? (
              <Typography
                sx={{ textAlign: "center", py: 4, color: "text.secondary" }}
              >
                {appliedFilters.length > 0 || searchText.trim()
                  ? "No users found matching the criteria"
                  : "No users available. Try applying filters or search."}
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                {userList.map((user) => (
                  <Box
                    key={user.userId}
                    sx={{
                      bgcolor: isUserSelected(user.userId)
                        ? "primary.50"
                        : "background.paper",
                      "&:hover": {
                        bgcolor: "grey.50",
                        borderColor: "grey.400",
                      },
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Checkbox
                      checked={isUserSelected(user.userId)}
                      onChange={() => handleUserToggle(user.userId)}
                      color="primary"
                    />
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 500, flex: 1 }}
                    >
                      {user.fullName}
                    </Typography>
                  </Box>
                ))}

                {/* Loader for next pages */}
                {userLoading && paginationModel.page > 0 && (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 2 }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
        {selectedUsers.length > 0 && (
          <Box sx={{ mt: 3, p: 2, bgcolor: "primary.50", borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Selected Users: {selectedUsers.length}
            </Typography>
          </Box>
        )}

        <Menu
          anchorEl={filterAnchorEl}
          open={filterMenuOpen}
          onClose={handleFilterMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem onClick={() => handleFilterTypeSelect("rank")}>
            Ranks
          </MenuItem>
          <MenuItem onClick={() => handleFilterTypeSelect("assembly")}>
            Assembly Constituency
          </MenuItem>
          <MenuItem onClick={() => handleFilterTypeSelect("zone")}>
            Zones
          </MenuItem>
          <MenuItem onClick={() => handleFilterTypeSelect("rio")}>
            RIOs
          </MenuItem>
        </Menu>

        {/* Filter Dialog */}
        {filterDialogOpen && (
          <FilterDialog
            open={filterDialogOpen}
            onClose={handleCancelFilters}
            onApply={handleApplyFilters}
            filterType={selectedFilterType}
            filterOptions={filterOptions}
            selectedFilterIds={selectedFilterIds}
            onSelectedFilterIdsChange={setSelectedFilterIds}
            filterListRef={filterListRef}
            handleFilterScroll={handleFilterScroll}
            filterSearchText={filterSearchText}
            setFilterSearchText={setFilterSearchText}
            filterLoading={filterLoading}
          />
        )}
      </CommonDialog>
    </Box>
  );
};

interface FilterDialogProps {
  open: boolean;
  onClose: () => void;
  onApply: () => void;
  filterType: "rank" | "assembly" | "zone" | "rio" | null;
  filterOptions: FilterOption[];
  selectedFilterIds: number[];
  onSelectedFilterIdsChange: (ids: number[]) => void;
  filterListRef: React.RefObject<HTMLDivElement>;
  handleFilterScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  filterSearchText: string;
  setFilterSearchText: (s: string) => void;
  filterLoading: boolean;
}

const FilterDialog: React.FC<FilterDialogProps> = ({
  open,
  onClose,
  onApply,
  filterType,
  filterOptions,
  selectedFilterIds,
  onSelectedFilterIdsChange,
  filterListRef,
  handleFilterScroll,
  filterSearchText,
  setFilterSearchText,
  filterLoading,
}) => {
  const handleToggle = (id: number) => {
    onSelectedFilterIdsChange(
      selectedFilterIds.includes(id)
        ? selectedFilterIds.filter((selectedId) => selectedId !== id)
        : [...selectedFilterIds, id]
    );
  };

  const title = {
    rank: "Select Ranks",
    assembly: "Select Assembly Constituencies",
    zone: "Select Zones",
    rio: "Select RIOs",
  }[filterType || "rank"];

  return (
    <>
      {open ? (
        <CommonDialog
          title={title}
          cancelButton={onClose}
          actionButtonTitle="Apply"
          cancelButtonTitle="Cancel"
          actionButton={onApply}
          disableActionButton={selectedFilterIds.length === 0}
        >
          <Box sx={{ mb: 1 }}>
            <TextField
              fullWidth
              placeholder={`Search ${title}`}
              value={filterSearchText}
              onChange={(e) => setFilterSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box
            sx={{ maxHeight: 400, overflow: "auto", mt: 1 }}
            ref={filterListRef}
            onScroll={handleFilterScroll}
          >
            {filterOptions.length === 0 && !filterLoading ? (
              <Typography
                sx={{ textAlign: "center", py: 4, color: "text.secondary" }}
              >
                No options available
              </Typography>
            ) : (
              <>
                {filterOptions.map((option) => (
                  <Box
                    key={option.id}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: 2,
                      borderBottom: "1px solid",
                      borderColor: "grey.200",
                    }}
                  >
                    <Typography>{option.name}</Typography>
                    <Checkbox
                      checked={selectedFilterIds.includes(option.id)}
                      onChange={() => handleToggle(option.id)}
                      color="primary"
                    />
                  </Box>
                ))}

                {filterLoading && (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 2 }}
                  >
                    <CircularProgress size={20} />
                  </Box>
                )}
              </>
            )}
          </Box>
        </CommonDialog>
      ) : null}
    </>
  );
};

export default CreateOrEditGroup;

const scrollStyles: SxProps = {
  maxHeight: 400,
  overflow: "auto",
  WebkitOverflowScrolling: "touch", // Smooth iOS
  "&::-webkit-scrollbar": { width: 8 },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    background: "#888",
    borderRadius: 4,
  },
};
